export type LocalChatMessage = {
    id: string
    role: 'user' | 'assistant'
    content: string
}

export type PromptSuggestion = {
    id: string
    title: string
    description: string
    prompt: string
}

export type ChatSessionInfo = {
    itemCount: number
    earliest?: string | null
    latest?: string | null
    summary: string
}

export type ReferencedContent = {
    contentId: number
}

export type Citation = {
    content_id: number
    quote: string
    relevance: string
}

export type ContentCategory = {
    name: string
    content_ids: number[]
    summary: string
}

export type StructuredAssistantResponse = {
    response: string
    citations: Citation[]
    categories?: ContentCategory[]
}

export type ParsedAssistantMessage = {
    isStructured: boolean
    text: string
    citations: Citation[]
    categories: ContentCategory[]
    rawContent: string
}

/**
 * Sanitizes error messages before displaying to users.
 * Removes potentially sensitive information like file paths, stack traces, and internal details.
 */
export function sanitizeErrorMessage(message: string): string {
    if (!message) {
        return 'An unexpected error occurred. Please try again.'
    }

    // Patterns that indicate sensitive/internal error info
    const sensitivePatterns = [
        /at\s+[\w.]+\s+\([^)]+:\d+:\d+\)/gi, // Stack trace lines
        /\/[\w/.-]+\.(rs|ts|js|tsx|jsx):\d+/gi, // File paths with line numbers
        /\/home\/[\w/.-]+/gi, // Unix home paths
        /\/Users\/[\w/.-]+/gi, // macOS user paths
        /C:\\[\w\\.-]+/gi, // Windows paths
        /RUST_BACKTRACE/gi, // Rust-specific
        /panicked at/gi, // Rust panic messages
        /thread '[\w-]+' panicked/gi, // Rust thread panic
        /stack backtrace:/gi, // Stack traces
        /Error\s*\{[^}]+\}/gi, // Debug error struct output
        /0x[0-9a-f]{8,}/gi // Memory addresses
    ]

    let sanitized = message

    // Check if message contains sensitive patterns
    const containsSensitiveInfo = sensitivePatterns.some((pattern) => pattern.test(message))

    if (containsSensitiveInfo) {
        // Extract only the first meaningful part before technical details
        const firstLine = message.split('\n')[0]
        const meaningfulPart = firstLine.split(':').slice(0, 2).join(':').trim()

        if (meaningfulPart.length > 10 && meaningfulPart.length < 200) {
            sanitized = meaningfulPart
        } else {
            sanitized = 'An internal error occurred while processing your request.'
        }
    }

    // Truncate very long messages
    if (sanitized.length > 300) {
        sanitized = sanitized.substring(0, 297) + '...'
    }

    return sanitized
}
